import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Header, Footer, ContactUsButton } from 'components';
import { AnimatePresence, motion } from 'framer-motion';
import { GlobalStyle } from 'shared_styles';
import { isMobileOnly } from 'react-device-detect';
import { useWindowSize, useIsomorphicLayoutEffect } from 'hooks';
import styled, { keyframes, css } from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SmootherContext } from 'context';
import '../../fonts/fonts.css';

function Layout({ children, location }) {
  const [smoother, setSmoother] = useState(null);
  const [userHasScrolled, setUserHasScrolled] = useState(false);
  const [showWebsiteOverlay, setShowWebsiteOverlay] = useState(false);
  const isMountedRef = useRef(true);
  const cookieYesTimeoutRef = useRef(null);
  const cookieYesListenerRef = useRef(null);

  useIsomorphicLayoutEffect(() => {
    // only load ScrollSmoother on omnichannel-healthcare-campaigns page, otherwise we get scroll
    // errors when navigating between homepage and the service pages. We can add other pages to the
    // below conditional if necessary.
    if (location.pathname === '/omnichannel-healthcare-campaigns/') {
      // ScrollSmoother is a premium GSAP plugin that requires a license.
      // For now, we'll disable it and use basic ScrollTrigger instead.
      gsap.registerPlugin(ScrollTrigger);
      setSmoother(null);
    } else if (smoother) {
      smoother.kill();
      setSmoother(null);
    }
  }, [location.pathname]);

  const { pathname } = location;
  const { windowWidth } = useWindowSize();

  const handleScroll = useCallback(() => {
    if (!isMountedRef.current) return;

    const hasScrolled = window.scrollY > 0;
    setUserHasScrolled(prevScrolled => {
      if (prevScrolled !== hasScrolled) {
        return hasScrolled;
      }
      return prevScrolled;
    });
  }, []);

  useEffect(() => {
    const initialScrollPosition = window.scrollY > 0;
    if (initialScrollPosition) {
      setUserHasScrolled(true);
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [pathname, handleScroll]);

  useEffect(() => {
    if (!showWebsiteOverlay && userHasScrolled) {
      const waitForCookieYes = () => {
        if (!isMountedRef.current) return;

        if (window.getCkyConsent) {
          const { isUserActionCompleted } = window.getCkyConsent();
          if (!isUserActionCompleted && isMountedRef.current) {
            setShowWebsiteOverlay(true);

            const handleConsentUpdate = () => {
              if (isMountedRef.current) {
                setShowWebsiteOverlay(false);
              }
              document.removeEventListener('cookieyes_consent_update', handleConsentUpdate);
            };

            cookieYesListenerRef.current = handleConsentUpdate;
            document.addEventListener('cookieyes_consent_update', handleConsentUpdate);
          }
        } else {
          cookieYesTimeoutRef.current = setTimeout(waitForCookieYes, 50);
        }
      };

      waitForCookieYes();
    }

    return () => {
      if (cookieYesTimeoutRef.current) {
        clearTimeout(cookieYesTimeoutRef.current);
        cookieYesTimeoutRef.current = null;
      }
      if (cookieYesListenerRef.current) {
        document.removeEventListener('cookieyes_consent_update', cookieYesListenerRef.current);
        cookieYesListenerRef.current = null;
      }
    };
  }, [userHasScrolled]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
      <SmootherContext.Provider value={smoother}>
        <GlobalStyle />
        <Header />
        <div id="smooth-content">
          <Wrapper
            id="top"
            pathname={pathname}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0 }}>
            <main>{children}</main>
            {/* <AnimatePresence>
              {!isMobileOnly && pathname !== '/contact/' && pathname !== '/logobook/' && (
                <ContactUsButton />
              )}
            </AnimatePresence> */}
            {pathname !== '/contact/' || (pathname === '/contact/' && windowWidth < 1200) ? (
              <Footer />
            ) : null}
          </Wrapper>
          {process.env.NODE_ENV !== 'development' && (
            <AnimatePresence>{showWebsiteOverlay && <WebsiteOverlay />}</AnimatePresence>
          )}
        </div>
      </SmootherContext.Provider>
    </>
  );
}

const WebsiteOverlay = styled(motion.div).attrs({
  initial: {
    opacity: 0
  },
  animate: {
    opacity: 1
  },
  exit: {
    opacity: 0
  },
  transition: {
    duration: 0.5
  }
})`
  background-color: rgba(000, 000, 000, 0.4);
  height: 100%;
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 7;
`;

const Wrapper = styled(motion.div)`
  background-color: ${({ pathname }) =>
    pathname === '/logobook/' ? 'var(--ax-black)' : 'var(--ax-beige)'};
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative !important;

  main > section {
    display: grid;
    grid-template-columns:
      1.25rem
      1fr
      1.25rem;

    @media screen and (min-width: 48rem) {
      grid-template-columns:
        6.25%
        1fr
        6.25%;
    }

    @media screen and (min-width: 90rem) {
      grid-template-columns:
        9%
        1fr
        9%;
    }
  }

  main {
    position: relative;
    grid-area: 1 / 1 / -1 / -1;
  }
`;

export default Layout;
